/**
 * Path Utilities for Safe URL Concatenation
 *
 * Handles:
 * - Double slashes: /apps/ui/ + /img → /apps/ui/img
 * - Missing slashes: /apps/ui + img → /apps/ui/img
 * - Empty segments: /apps + '' + /img → /apps/img
 * - Protocol preservation: http://, https://, //
 * - Null/undefined safety
 *
 * Does NOT support:
 * - Dot segment resolution (., ..)
 * - Filesystem-style path traversal
 * - Relative path navigation
 */

/**
 * Combines path segments safely, handling leading/trailing slashes
 *
 * Features:
 * - Normalizes slashes (removes double slashes)
 * - Preserves absolute path (leading /)
 * - Preserves protocol (http://, https://, //)
 * - Filters empty/null/undefined segments
 * - Validates no query strings (use fetch params)
 *
 * @param parts - Path segments to combine
 * @returns Combined path with normalized slashes
 *
 * @example
 * pathCombine('/apps/ui/', '/img/logo.png') → '/apps/ui/img/logo.png'
 * pathCombine('/apps/ui', 'img/logo.png') → '/apps/ui/img/logo.png'
 * pathCombine('/', '/img/logo.png') → '/img/logo.png'
 * pathCombine('http://api.com/', '/users') → 'http://api.com/users'
 */
export const pathCombine = (...parts: (string | null | undefined)[]): string => {
  // Filter out null, undefined, and empty strings
  const validParts = parts.filter((part): part is string => part != null && part !== "");

  // Return empty string if no valid parts
  if (validParts.length === 0) {
    return "";
  }

  // Validate: query strings/fragments only allowed in last segment
  for (let i = 0; i < validParts.length - 1; i++) {
    const part = validParts[i]!; // Safe: filtered above
    if (part.includes("?") || part.includes("#")) {
      throw new Error(
        `[pathCombine] Query strings (?) and fragments (#) are not allowed in path segments except the last one. ` +
          `Found in segment ${i + 1}: "${part}". ` +
          `Move query parameters to the final segment.`,
      );
    }
  }

  // Process first part separately to preserve protocols and leading slashes
  let result = validParts[0]!; // Safe: length check above ensures this exists

  // Detect and preserve protocol (http://, https://, //)
  const protocolMatch = result.match(/^(https?:\/\/|\/\/)/);
  const protocol = protocolMatch ? protocolMatch[0] : "";

  if (protocol) {
    // Remove protocol temporarily for processing
    result = result.slice(protocol.length);
  }

  // Detect if path should be absolute (starts with /)
  const isAbsolute = !protocol && result.startsWith("/");

  // Strip trailing slashes from first part
  result = result.replace(/\/+$/, "");

  // Process remaining parts
  for (let i = 1; i < validParts.length; i++) {
    let part = validParts[i]!; // Safe: within bounds

    // Don't strip slashes from last segment if it contains query string
    const isLastSegment = i === validParts.length - 1;
    const hasQueryOrFragment = part.includes("?") || part.includes("#");

    if (isLastSegment && hasQueryOrFragment) {
      // Split on ? or # to preserve query/fragment
      const splitIndex = Math.min(
        part.indexOf("?") !== -1 ? part.indexOf("?") : part.length,
        part.indexOf("#") !== -1 ? part.indexOf("#") : part.length,
      );
      const pathPart = part.slice(0, splitIndex);
      const queryPart = part.slice(splitIndex);

      // Strip leading/trailing slashes from path part only
      const cleanedPath = pathPart.replace(/^\/+/, "").replace(/\/+$/, "");

      if (cleanedPath.length > 0) {
        result = `${result}/${cleanedPath}${queryPart}`;
      } else {
        result = `${result}${queryPart}`;
      }
      continue;
    }

    // Strip leading and trailing slashes from middle segments
    part = part.replace(/^\/+/, "").replace(/\/+$/, "");

    // Skip empty segments after cleaning
    if (part === "") {
      continue;
    }

    // Warn in dev mode if absolute URL appears after first part
    if (
      import.meta.dev &&
      i > 0 &&
      (part.startsWith("http://") || part.startsWith("https://") || part.startsWith("//"))
    ) {
      console.warn(
        `[pathCombine] Absolute URL detected in position ${i + 1}: "${part}". ` +
          `This will be treated as a path segment, not a full URL. ` +
          `Did you mean to use this as the first argument?`,
      );
    }

    // Append part with single slash separator
    result = `${result}/${part}`;
  }

  // Ensure absolute path has leading slash
  if (isAbsolute && !result.startsWith("/")) {
    result = "/" + result;
  }

  // Restore protocol
  if (protocol) {
    result = protocol + result;
  }

  return result;
};

/**
 * Combines base URL with endpoint path for API calls
 *
 * Specifically designed for API endpoints where:
 * - Base URL might have a path: https://api.com/v1
 * - Endpoint is relative: /users or users
 *
 * @param baseUrl - Base URL without query string (with or without trailing slash)
 * @param endpoint - Endpoint path (with or without leading slash)
 * @returns Combined URL
 */
export const apiUrlCombine = (baseUrl: string | null | undefined, endpoint: string): string => {
  // Validate base URL is provided
  if (!baseUrl || baseUrl.trim() === "") {
    throw new Error(
      `[apiUrlCombine] Base URL cannot be empty or undefined. ` +
        `Ensure API_BASE_URL is properly configured in runtime config. ` +
        `Received: ${JSON.stringify(baseUrl)}`,
    );
  }

  const trimmedBase = baseUrl.trim();

  // Validate base URL has no query strings or fragments
  if (trimmedBase.includes("?") || trimmedBase.includes("#")) {
    const hasQuery = trimmedBase.includes("?");
    const hasHash = trimmedBase.includes("#");
    const problemType =
      hasQuery && hasHash
        ? "query parameters and hash fragments"
        : hasQuery
          ? "query parameters"
          : "hash fragments";

    throw new Error(
      `[apiUrlCombine] Base URL should not contain ${problemType}. ` +
        `Found: "${trimmedBase}". ` +
        `The base URL should be clean (e.g., "https://api.example.com" or "/api/v1"). ` +
        `Add query parameters to the endpoint instead.`,
    );
  }

  // Clean endpoint: remove multiple leading slashes
  const cleanedEndpoint = endpoint.replace(/^\/+/, "/");

  // Use pathCombine for the actual concatenation
  return pathCombine(trimmedBase, cleanedEndpoint);
};
